import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Plus, Download, Search, ChevronDown, ChevronUp,
  FileText, Eye, Trash2, Loader2, AlertCircle,
  Check, X, Zap, Fuel, Flame, Droplets, SlidersHorizontal, Leaf, History, Paperclip, MoreVertical, Settings, Clock, Building2, Edit,
  BarChart3, TrendingUp, ShoppingCart, CheckCircle2, Activity, Calendar, CalendarRange, Camera, Upload, RefreshCw, ScanLine
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import { calculateEmissions, getScope } from '@/utils/emissionCalculator';
import {
  formatActivityDate,
  formatEntryDate,
  getReportingPeriods,
  getFinancialYear,
  getFYLabel
} from '@/utils/financialYearUtils';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';
import CsvImporter from '@/components/CsvImporter';
import ReceiptScanner from '@/components/ReceiptScanner';
import { useEmissionsData } from '@/hooks/useEmissionsData';
import { usePermissions, FEATURES } from '@/permissions/usePermissions';
import { useVisibleCategories } from '@/hooks/useVisibleCategories';

// Categories that are typically billed over a period (utility bills)
const periodBasedCategories = ['electricity', 'electricity_greenpower', 'natural_gas', 'water'];

const EntriesPage = () => {
  const navigate = useNavigate();
  const { currentOrg, user, userData } = useOutletContext();
  const { canWithReason } = usePermissions();
  const {
    groupedCategories = {},
    flatCategories = [],
    loading: categoriesLoading
  } = useVisibleCategories(currentOrg);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Helper to get category info from dynamic categories or legacy fallback
  const getCategoryInfo = useCallback((categoryValue) => {
    const found = flatCategories.find(c => c.value === categoryValue);
    if (found) return found;

    // Fallback for legacy or unknown categories
    const legacyMap = {
      fuel: { label: 'Vehicle Fuel', icon: Fuel, color: '#0d5c2e', scope: 'Scope 1', unit: 'L' },
      waste: { label: 'Waste', icon: Trash2, color: '#d97706', scope: 'Scope 3', unit: 'tonnes' },
      lpg: { label: 'LPG', icon: Flame, color: '#ea580c', scope: 'Scope 1', unit: 'kg' },
      offset: { label: 'Carbon Offset', icon: Leaf, color: '#74A57F', scope: 'Offset', unit: 'tCO₂-e' }
    };
    return legacyMap[categoryValue] || { label: categoryValue, icon: Zap, color: '#64748b', scope: 'Unknown', unit: '' };
  }, [flatCategories]);

  const { addEmissionEntry, updateEmissionEntry } = useEmissionsData(currentOrg, user, userData);

  // Filtering
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [sortField, setSortField] = useState('entry_date');
  const [sortDirection, setSortDirection] = useState('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // UI
  const [showFilters, setShowFilters] = useState(false);
  const [showCarbonCredits, setShowCarbonCredits] = useState(false);
  const [deletingEntry, setDeletingEntry] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showCsvImporter, setShowCsvImporter] = useState(false);
  const [showReceiptScanner, setShowReceiptScanner] = useState(false);
  const [addingEntry, setAddingEntry] = useState(false);
  const [openActionMenu, setOpenActionMenu] = useState(null); // For kebab menu
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showEvidenceDeleteConfirm, setShowEvidenceDeleteConfirm] = useState(null);
  const [showEvidenceUpload, setShowEvidenceUpload] = useState(null);
  const [selectedEvidenceFile, setSelectedEvidenceFile] = useState(null);
  const [evidenceUploading, setEvidenceUploading] = useState(false);
  const [deletingEvidence, setDeletingEvidence] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(null);
  const [entryHistory, setEntryHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const evidenceInputRef = useRef(null);

  // Camera capture state
  const [cameraMode, setCameraMode] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(null);
  const [editFormData, setEditFormData] = useState({
    category: '',
    quantity: '',
    start_date: '',
    end_date: '',
    is_date_range: false,
    notes: ''
  });
  const [editLoading, setEditLoading] = useState(false);

  const [newEntry, setNewEntry] = useState({
    category: 'electricity',
    quantity: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    is_date_range: true, // Default to date range since electricity is default category
    notes: ''
  });

  // Handle category change for Add form - auto-toggle to date range for utility categories
  const handleNewEntryCategoryChange = (category) => {
    const shouldBeRange = periodBasedCategories.includes(category);
    setNewEntry(prev => ({
      ...prev,
      category,
      is_date_range: shouldBeRange,
      end_date: shouldBeRange ? prev.end_date || prev.start_date : ''
    }));
  };

  // Handle category change for Edit form - auto-toggle to date range for utility categories
  const handleEditCategoryChange = (category) => {
    const shouldBeRange = periodBasedCategories.includes(category);
    setEditFormData(prev => ({
      ...prev,
      category,
      is_date_range: shouldBeRange,
      end_date: shouldBeRange ? prev.end_date || prev.start_date : ''
    }));
  };

  // Calculate financial year from date (July = start of new FY) - now uses utility
  const getFinancialYearFromDate = (dateStr) => {
    if (!dateStr) return new Date().getFullYear();
    return getFinancialYear(new Date(dateStr));
  };

  // Format datetime for display with timezone based on org state
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);

    // Map Australian states to timezone strings
    const stateTimezones = {
      'NSW': 'Australia/Sydney',
      'VIC': 'Australia/Melbourne',
      'QLD': 'Australia/Brisbane',
      'SA': 'Australia/Adelaide',
      'WA': 'Australia/Perth',
      'TAS': 'Australia/Hobart',
      'NT': 'Australia/Darwin',
      'ACT': 'Australia/Sydney'
    };

    const timezone = stateTimezones[currentOrg?.state] || 'Australia/Sydney';

    return date.toLocaleString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: timezone
    });
  };

  // Handle opening edit modal
  const handleOpenEdit = (entry) => {
    const startDate = entry.raw?.start_date || entry.date;
    const endDate = entry.raw?.end_date;
    const isRange = endDate && startDate !== endDate;

    setEditFormData({
      category: entry.category,
      quantity: entry.quantity?.toString() || '',
      start_date: startDate ? new Date(startDate).toISOString().split('T')[0] : '',
      end_date: endDate ? new Date(endDate).toISOString().split('T')[0] : '',
      is_date_range: isRange,
      notes: entry.raw?.notes || ''
    });
    setShowEditModal(entry);
    setOpenActionMenu(null);
  };

  // Handle edit submission - Direct Supabase
  const handleEditSubmit = async () => {
    if (!showEditModal || !currentOrg) return;

    // Validate date range
    if (editFormData.is_date_range && editFormData.end_date) {
      if (new Date(editFormData.end_date) < new Date(editFormData.start_date)) {
        toast.error('End date cannot be before start date');
        return;
      }
    }

    try {
      const categoryInfo = getCategoryInfo(editFormData.category);

      const updateData = {
        category: editFormData.category,
        quantity: editFormData.quantity,
        start_date: editFormData.start_date,
        end_date: editFormData.is_date_range ? editFormData.end_date : null,
        is_date_range: editFormData.is_date_range,
        notes: editFormData.notes
      };

      await updateEmissionEntry(
        showEditModal.id,
        updateData,
        categoryInfo,
        {
          ...showEditModal,
          category: showEditModal.category,
          quantity: showEditModal.quantity,
          notes: showEditModal.raw?.notes,
          start_date: showEditModal.raw?.start_date || showEditModal.date,
          end_date: showEditModal.raw?.end_date
        },
        () => {
          setShowEditModal(null);
          setTimeout(() => fetchData(), 500); // Small delay to ensure DB commit before refetching
        }
      );
    } catch (error) {
      console.error('Error updating entry:', error);
      // toast is already handled inside the hook
    }
  };

  const handleAddEntry = async () => {
    if (!newEntry.quantity || parseFloat(newEntry.quantity) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    if (!currentOrg) {
      toast.error('No currentOrg found');
      return;
    }

    // Validate date range
    if (newEntry.is_date_range && newEntry.end_date) {
      if (new Date(newEntry.end_date) < new Date(newEntry.start_date)) {
        toast.error('End date cannot be before start date');
        return;
      }
    }

    try {
      const categoryInfo = getCategoryInfo(newEntry.category);

      const formData = {
        category: newEntry.category,
        quantity: newEntry.quantity,
        start_date: newEntry.start_date,
        end_date: newEntry.end_date,
        is_date_range: newEntry.is_date_range,
        notes: newEntry.notes,
        // Optional: you can structure receipt handling if EntriesPage eventually uses it
      };

      await addEmissionEntry(
        formData,
        categoryInfo,
        () => {
          setShowAddForm(false);
          setNewEntry({
            category: 'electricity',
            quantity: '',
            start_date: new Date().toISOString().split('T')[0],
            end_date: '',
            is_date_range: false,
            notes: ''
          });
          setTimeout(() => fetchData(), 500); // Small delay to ensure DB commit before refetching
        }
      );
    } catch (error) {
      console.error('Error adding entry:', error);
      // toast is already handled inside the hook
    }
  };

  useEffect(() => {
    if (currentOrg) fetchData();
  }, [currentOrg?.id]);

  const fetchData = async () => {
    if (!currentOrg) return;
    try {

      // Fetch emissions with documents
      const { data: emissionsData, error: emissionsError } = await supabase
        .from('emissions_data')
        .select(`
          *,
          documents(id, filename, storage_path, document_type, file_size)
        `)
        .eq('organisation_id', currentOrg.id)
        .order('entry_date', { ascending: false });

      if (emissionsError) throw emissionsError;

      // Fetch offsets with allocations
      const { data: offsetsData } = await supabase
        .from('carbon_offsets')
        .select(`
          *,
          offset_allocations (*)
        `)
        .eq('organisation_id', currentOrg.id);

      // Combine into unified entries
      const emissionEntries = (emissionsData || []).map(e => ({
        id: e.id, type: 'emission', name: getCategoryInfo(e.category).label,
        category: e.category, amount: e.co2_equivalent || 0, date: e.entry_date,
        year: e.financial_year, hasEvidence: e.documents?.length > 0,
        evidenceUrl: e.documents?.[0]?.storage_url,
        storagePath: e.documents?.[0]?.storage_path,
        raw: e,
        quantity: e.quantity, unit: e.unit
      }));

      // Carbon Credit Purchases
      const purchaseEntries = (offsetsData || []).map(o => ({
        id: `purchase-${o.id}`, type: 'offset_purchase', name: o.project_name,
        category: 'offset', amount: o.amount_tco2e || 0, date: o.purchase_date || o.created_at,
        year: null, hasEvidence: !!o.certificate_url,
        evidenceUrl: o.certificate_url, raw: o,
        quantity: o.amount_tco2e, unit: 'tCO₂-e'
      }));

      // Carbon Credit Allocations (from allocations within each offset)
      const allocationEntries = (offsetsData || []).flatMap(o =>
        (o.offset_allocations || []).map(alloc => ({
          id: `alloc-${alloc.id}`, type: 'offset_applied', name: o.project_name,
          category: 'offset', amount: -(alloc.amount_tco2e || 0), date: alloc.created_at,
          year: alloc.financial_year, hasEvidence: !!o.certificate_url,
          evidenceUrl: o.certificate_url, raw: { ...alloc, project_name: o.project_name },
          quantity: alloc.amount_tco2e, unit: 'tCO₂-e'
        }))
      );

      setEntries([...emissionEntries, ...purchaseEntries, ...allocationEntries]);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!currentOrg) return;

    setDeletingEntry(entry.id);
    try {
      if (entry.type === 'offset_purchase') {
        // Delete carbon offset
        const offsetId = entry.id.replace('purchase-', '');

        // First delete allocations
        await supabase
          .from('offset_allocations')
          .delete()
          .eq('carbon_offset_id', offsetId);

        // Then delete offset
        const { error } = await supabase
          .from('carbon_offsets')
          .delete()
          .eq('id', offsetId);

        if (error) throw error;
      } else if (entry.type === 'offset_applied') {
        toast.error('Carbon credit allocations cannot be deleted from this view');
        return;
      } else {
        // Delete emission
        // First delete any associated documents
        await supabase
          .from('documents')
          .delete()
          .eq('emission_entry_id', entry.id);

        // Then delete the emission
        const { error } = await supabase
          .from('emissions_data')
          .delete()
          .eq('id', entry.id);

        if (error) throw error;

        // Add audit log
        await supabase.from('audit_logs').insert({
          id: crypto.randomUUID(),
          organisation_id: currentOrg.id,
          resource_type: 'emission',
          resource_id: entry.id,
          action: 'delete',
          description: `Deleted ${entry.name} entry`
        });
      }

      setEntries(entries.filter(e => e.id !== entry.id));
      toast.success('Deleted');
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.message || 'Delete failed');
    } finally {
      setDeletingEntry(null);
    }
  };

  // Export to CSV with optional year filter
  const exportToCSV = async (forYear = null) => {
    setExportLoading(true);
    try {
      // Filter entries to export (emissions only)
      let emissionsToExport = entries.filter(e => e.type === 'emission');

      if (forYear && forYear > 0) {
        emissionsToExport = emissionsToExport.filter(e => e.year === forYear);
      }

      if (emissionsToExport.length === 0) {
        toast.error('No emissions data to export');
        setExportLoading(false);
        return;
      }

      const scopeMapping = {
        'fuel_petrol': 'Scope 1', 'fuel_diesel': 'Scope 1', 'fuel_lpg': 'Scope 1', 'natural_gas': 'Scope 1',
        'electricity': 'Scope 2', 'electricity_greenpower': 'Scope 2',
        'flight_short': 'Scope 3', 'flight_medium': 'Scope 3', 'flight_long': 'Scope 3',
        'hotel_stay': 'Scope 3', 'waste_landfill': 'Scope 3', 'waste_recycling': 'Scope 3',
        'waste_compost': 'Scope 3', 'water': 'Scope 3'
      };

      const csvData = emissionsToExport.map(e => ({
        'Entry Date': e.date ? new Date(e.date).toLocaleDateString('en-AU') : '',
        'Category': e.name || getCategoryInfo(e.category).label || e.category,
        'Scope': scopeMapping[e.category] || 'Scope 3',
        'Quantity': e.quantity,
        'Unit': e.unit,
        'CO2 Equivalent (tonnes)': e.amount ? e.amount.toFixed(6) : '',
        'Financial Year': e.year || '',
        'Notes': e.raw?.notes || '',
        'Created At': e.raw?.created_at ? new Date(e.raw.created_at).toLocaleString('en-AU') : ''
      }));

      const csv = Papa.unparse(csvData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const filename = forYear && forYear > 0 ? `emissions_FY${forYear}.csv` : 'emissions_all_years.csv';
      saveAs(blob, filename);
      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed: ' + (error.message || 'Unknown error'));
    } finally {
      setExportLoading(false);
    }
  };

  // Export to XLS with optional year filter
  const exportToXLS = async (forYear = null) => {
    setExportLoading(true);
    try {
      let emissionsToExport = entries.filter(e => e.type === 'emission');

      if (forYear && forYear > 0) {
        emissionsToExport = emissionsToExport.filter(e => e.year === forYear);
      }

      if (emissionsToExport.length === 0) {
        toast.error('No emissions data to export');
        setExportLoading(false);
        return;
      }

      const scopeMapping = {
        'fuel_petrol': 'Scope 1', 'fuel_diesel': 'Scope 1', 'fuel_lpg': 'Scope 1', 'natural_gas': 'Scope 1',
        'electricity': 'Scope 2', 'electricity_greenpower': 'Scope 2',
        'flight_short': 'Scope 3', 'flight_medium': 'Scope 3', 'flight_long': 'Scope 3',
        'hotel_stay': 'Scope 3', 'waste_landfill': 'Scope 3', 'waste_recycling': 'Scope 3',
        'waste_compost': 'Scope 3', 'water': 'Scope 3'
      };

      let html = '<html><head><meta charset="UTF-8"></head><body>';
      html += '<table border="1">';
      html += '<tr><th>Entry Date</th><th>Category</th><th>Scope</th><th>Quantity</th><th>Unit</th><th>CO2 Equivalent (tonnes)</th><th>Financial Year</th><th>Notes</th><th>Created At</th></tr>';

      emissionsToExport.forEach(e => {
        html += '<tr>';
        html += `<td>${e.date ? new Date(e.date).toLocaleDateString('en-AU') : ''}</td>`;
        html += `<td>${e.name || getCategoryInfo(e.category).label || e.category}</td>`;
        html += `<td>${scopeMapping[e.category] || 'Scope 3'}</td>`;
        html += `<td>${e.quantity}</td>`;
        html += `<td>${e.unit}</td>`;
        html += `<td>${e.amount ? e.amount.toFixed(6) : ''}</td>`;
        html += `<td>${e.year || ''}</td>`;
        html += `<td>${e.raw?.notes || ''}</td>`;
        html += `<td>${e.raw?.created_at ? new Date(e.raw.created_at).toLocaleString('en-AU') : ''}</td>`;
        html += '</tr>';
      });

      html += '</table></body></html>';

      const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8' });
      const filename = forYear && forYear > 0 ? `emissions_FY${forYear}.xls` : 'emissions_all_years.xls';
      saveAs(blob, filename);
      toast.success('Excel file exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed: ' + (error.message || 'Unknown error'));
    } finally {
      setExportLoading(false);
    }
  };

  // Camera functions for evidence capture
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' } // Use back camera on mobile
      });
      setCameraStream(stream);
      setCameraMode(true);
      setCapturedImage(null);

      // Wait for video element to be ready
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (err) {
      console.error('Camera error:', err);
      toast.error('Could not access camera. Please check permissions.');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setCameraMode(false);
    setCapturedImage(null);
  }, [cameraStream]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw current video frame to canvas
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    // Convert to blob
    canvas.toBlob((blob) => {
      if (blob) {
        setCapturedImage(blob);
        // Stop the camera stream after capture
        if (cameraStream) {
          cameraStream.getTracks().forEach(track => track.stop());
          setCameraStream(null);
        }
      }
    }, 'image/jpeg', 0.9);
  }, [cameraStream]);

  const retakePhoto = useCallback(() => {
    setCapturedImage(null);
    startCamera();
  }, [startCamera]);

  const uploadCapturedImage = async (emissionId) => {
    if (!capturedImage) return;

    setEvidenceUploading(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('supabase_user_id', authUser.id)
        .single();

      const filePath = `${currentOrg.id}/${emissionId}/evidence_${Date.now()}.jpg`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('evidence-vault')
        .upload(filePath, capturedImage);

      if (uploadError) throw uploadError;

      // Create document record
      await supabase.from('documents').insert({
        id: crypto.randomUUID(),
        organisation_id: currentOrg.id,
        user_id: userData?.id,
        emission_entry_id: emissionId,
        filename: `photo_${Date.now()}.jpg`,
        file_type: 'image/jpeg',
        file_size: capturedImage.size,
        storage_path: filePath,
        document_type: 'evidence'
      });

      // Get emission details for history record
      const { data: emissionData } = await supabase
        .from('emissions_data')
        .select('category, quantity, unit, co2_equivalent, entry_date')
        .eq('id', emissionId)
        .single();

      // Create entry history record for evidence attachment
      if (emissionData) {
        await supabase.from('entry_history').insert({
          id: crypto.randomUUID(),
          emission_entry_id: emissionId,
          organisation_id: currentOrg.id,
          user_id: userData?.id,
          action: 'evidence_added',
          category: emissionData.category,
          quantity: emissionData.quantity,
          unit: emissionData.unit,
          co2_equivalent: emissionData.co2_equivalent,
          entry_date: emissionData.entry_date,
          notes: 'Photo captured and attached as evidence',
          user_email: authUser?.email
        });
      }

      // Create audit log for currentOrg activity
      await supabase.from('audit_logs').insert({
        id: crypto.randomUUID(),
        user_id: userData?.id,
        user_email: authUser?.email,
        organisation_id: currentOrg.id,
        resource_type: 'evidence',
        resource_id: emissionId,
        action: 'create',
        description: 'Captured and attached photo evidence'
      });

      toast.success('Photo evidence uploaded successfully');
      setShowEvidenceUpload(null);
      setCapturedImage(null);
      setCameraMode(false);
      fetchData();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload photo');
    } finally {
      setEvidenceUploading(false);
    }
  };

  // Cleanup camera on modal close
  const closeEvidenceModal = useCallback(() => {
    stopCamera();
    setShowEvidenceUpload(null);
    setSelectedEvidenceFile(null);
    setCapturedImage(null);
    setCameraMode(false);
  }, [stopCamera]);

  // Handle evidence upload
  const handleEvidenceUpload = async (file, emissionId) => {
    if (!file) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const allowedExts = ['pdf', 'jpg', 'jpeg', 'png', 'webp'];
    const fileExt = file.name.split('.').pop().toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExts.includes(fileExt)) {
      toast.error('File must be PDF, JPG, PNG, or WebP');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be less than 10MB');
      return;
    }

    setEvidenceUploading(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('supabase_user_id', authUser.id)
        .single();

      const fileExt = file.name.split('.').pop();
      const filePath = `${currentOrg.id}/${emissionId}/evidence_${Date.now()}.${fileExt}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('evidence-vault')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Create document record
      await supabase.from('documents').insert({
        id: crypto.randomUUID(),
        organisation_id: currentOrg.id,
        user_id: userData?.id,
        emission_entry_id: emissionId,
        filename: file.name,
        file_type: file.type,
        file_size: file.size,
        storage_path: filePath,
        document_type: 'evidence'
      });

      // Get emission details for history record
      const { data: emissionData } = await supabase
        .from('emissions_data')
        .select('category, quantity, unit, co2_equivalent, entry_date')
        .eq('id', emissionId)
        .single();

      // Create entry history record for evidence attachment
      if (emissionData) {
        await supabase.from('entry_history').insert({
          id: crypto.randomUUID(),
          emission_entry_id: emissionId,
          organisation_id: currentOrg.id,
          user_id: userData?.id,
          action: 'evidence_added',
          category: emissionData.category,
          quantity: emissionData.quantity,
          unit: emissionData.unit,
          co2_equivalent: emissionData.co2_equivalent,
          entry_date: emissionData.entry_date,
          notes: `Attached evidence: ${file.name}`,
          user_email: authUser?.email
        });
      }

      // Create audit log for currentOrg activity
      await supabase.from('audit_logs').insert({
        id: crypto.randomUUID(),
        user_id: userData?.id,
        user_email: authUser?.email,
        organisation_id: currentOrg.id,
        resource_type: 'evidence',
        resource_id: emissionId,
        action: 'create',
        description: `Uploaded evidence: ${file.name}`,
        new_data: JSON.stringify({
          filename: file.name,
          file_type: file.type,
          emission_entry_id: emissionId
        })
      });

      toast.success('Evidence uploaded successfully!');
      fetchData(); // Refresh the list
      setSelectedEvidenceFile(null);
      setShowEvidenceUpload(null);
    } catch (error) {
      console.error('Error uploading evidence:', error);
      toast.error(error.message || 'Failed to upload evidence');
    } finally {
      setEvidenceUploading(false);
      if (evidenceInputRef.current) {
        evidenceInputRef.current.value = '';
      }
    }
  };

  // Fetch entry history
  const fetchEntryHistory = async (emissionId) => {
    setHistoryLoading(true);
    setShowHistoryModal(emissionId);
    try {
      const { data, error } = await supabase
        .from('entry_history')
        .select('*')
        .eq('emission_entry_id', emissionId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEntryHistory(data || []);
    } catch (error) {
      console.error('Error fetching entry history:', error);
      toast.error('Failed to load data history');
      setEntryHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Delete evidence from an entry
  const handleDeleteEvidence = async (entry) => {
    if (!entry.raw?.documents || entry.raw.documents.length === 0) {
      toast.error('No evidence to delete');
      setShowEvidenceDeleteConfirm(null);
      return;
    }

    setDeletingEvidence(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('supabase_user_id', authUser.id)
        .single();

      // Get all documents for this entry
      const documents = entry.raw.documents;

      // Delete files from storage
      for (const doc of documents) {
        if (doc.storage_path) {
          await supabase.storage.from('evidence-vault').remove([doc.storage_path]);
        }
      }

      // Delete document records
      const docIds = documents.map(d => d.id);
      await supabase.from('documents').delete().in('id', docIds);

      // Log to entry history
      await supabase.from('entry_history').insert({
        id: crypto.randomUUID(),
        emission_entry_id: entry.id,
        organisation_id: currentOrg.id,
        user_id: userData?.id,
        action: 'evidence_removed',
        category: entry.category,
        quantity: entry.quantity,
        unit: entry.unit,
        co2_equivalent: entry.amount,
        entry_date: entry.date,
        notes: `Removed ${documents.length} evidence file(s)`,
        user_email: authUser?.email
      });

      // Create audit log for currentOrg activity
      await supabase.from('audit_logs').insert({
        id: crypto.randomUUID(),
        user_id: userData?.id,
        user_email: authUser?.email,
        organisation_id: currentOrg.id,
        resource_type: 'evidence',
        resource_id: entry.id,
        action: 'delete',
        description: `Deleted ${documents.length} evidence file(s)`,
        previous_data: JSON.stringify({
          filenames: documents.map(d => d.filename),
          emission_entry_id: entry.id
        })
      });

      toast.success('Evidence deleted successfully');
      fetchData(); // Refresh the list
      setOpenActionMenu(null);
      setShowEvidenceDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting evidence:', error);
      toast.error(error.message || 'Failed to delete evidence');
    } finally {
      setDeletingEvidence(false);
    }
  };

  // Filter - handle all three types
  const filtered = entries.filter(e => {
    // Hide non-emissions if toggle is off
    if (!showCarbonCredits && e.type !== 'emission') return false;

    // Handle type filter - group offset_purchase and offset_applied under "offset" for backward compatibility
    let matchesType = filterType === '';
    if (filterType === 'emission') {
      matchesType = e.type === 'emission';
    } else if (filterType === 'offset') {
      matchesType = e.type === 'offset_purchase' || e.type === 'offset_applied';
    } else if (filterType === 'offset_purchase') {
      matchesType = e.type === 'offset_purchase';
    } else if (filterType === 'offset_applied') {
      matchesType = e.type === 'offset_applied';
    }

    const matchesSearch = searchTerm === '' || e.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesYear = filterYear === '' || e.year?.toString() === filterYear;
    const matchesCategory = filterCategory === '' || e.category === filterCategory;
    return matchesType && matchesSearch && matchesYear && matchesCategory;
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    let aVal, bVal;
    if (sortField === 'date') {
      aVal = new Date(a.raw?.start_date || a.date);
      bVal = new Date(b.raw?.start_date || b.date);
    }
    else if (sortField === 'amount') { aVal = Math.abs(a.amount); bVal = Math.abs(b.amount); }
    else if (sortField === 'name') { aVal = (a.name || '').toLowerCase(); bVal = (b.name || '').toLowerCase(); }
    else if (sortField === 'quantity') { aVal = parseFloat(a.quantity) || 0; bVal = parseFloat(b.quantity) || 0; }
    else if (sortField === 'reporting_period') {
      aVal = getReportingPeriods(a.raw?.start_date || a.date, a.raw?.end_date);
      bVal = getReportingPeriods(b.raw?.start_date || b.date, b.raw?.end_date);
    }
    else if (sortField === 'entry_date') {
      aVal = new Date(a.raw?.entry_date || a.raw?.created_at || a.date);
      bVal = new Date(b.raw?.entry_date || b.raw?.created_at || b.date);
    }
    else if (sortField === 'type') {
      aVal = a.type || '';
      bVal = b.type || '';
    }
    else { aVal = a.name; bVal = b.name; }

    // Handle null/undefined values
    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;

    return sortDirection === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
  });

  // Paginate
  const totalPages = Math.ceil(sorted.length / rowsPerPage);
  const paginated = sorted.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  // Get unique values for filters
  const years = [...new Set(entries.map(e => e.year).filter(Boolean))].sort((a, b) => b - a);
  const categories = [...new Set(entries.map(e => e.category))].filter(c => c !== 'offset');

  const handleSort = (field) => {
    if (sortField === field) setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp className="w-4 h-4 inline ml-1" /> : <ChevronDown className="w-4 h-4 inline ml-1" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] via-[#EDF6F9] to-[#E2E8F0] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#003B5C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] via-[#EDF6F9] to-[#E2E8F0]" data-testid="entries-page">
      {/* Role-based access warning for restricted users */}
      {canWithReason(FEATURES.EMISSIONS_ADD_ENTRY).isHidden && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center space-x-3">
            <Eye className="w-5 h-5 text-slate-400" />
            <p className="text-sm text-slate-600">
              <span className="font-medium">View Only Access</span> — You can view emissions data and export reports. Contact the owner for editing permissions.
            </p>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#003B5C] flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <History className="w-7 h-7 mr-3 text-[#74A57F]" />
              All Entries
            </h1>
            <p className="text-slate-600 mt-1">{currentOrg?.name} • {filtered.length} entries</p>
          </div>

          {/* Header Action Buttons */}
          <div className="flex items-center space-x-3">
            {!canWithReason(FEATURES.EMISSIONS_OCR_IMPORT).isHidden && (
              <Button
                onClick={() => setShowReceiptScanner(true)}
                variant="outline"
                className="border-[#003B5C] text-[#003B5C] hover:bg-[#003B5C]/5"
              >
                <ScanLine className="w-4 h-4 mr-2" /> Scan
              </Button>
            )}
            {!canWithReason(FEATURES.EMISSIONS_BULK_CSV).isHidden && (
              <Button
                onClick={() => setShowCsvImporter(true)}
                variant="outline"
                className="border-[#74A57F] text-[#74A57F] hover:bg-[#74A57F]/10"
              >
                <Upload className="w-4 h-4 mr-2" /> Import
              </Button>
            )}
            {!canWithReason(FEATURES.EMISSIONS_ADD_ENTRY).isHidden && (
              <Button
                onClick={() => setShowAddForm(true)}
                variant="outline"
                className="border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600"
              >
                <Flame className="w-4 h-4 mr-2" /> Add Emissions
              </Button>
            )}
            {showCarbonCredits && !canWithReason(FEATURES.EMISSIONS_ADD_CARBON_CREDIT).isHidden && (
              <Button
                onClick={() => navigate('/offsets')}
                variant="outline"
                className="border-[#74A57F] text-[#74A57F] hover:bg-[#74A57F] hover:text-white"
              >
                <Leaf className="w-4 h-4 mr-2" /> Add Credit
              </Button>
            )}
          </div>
        </div>

        {/* Type Toggle + Action Buttons */}
        <div className="flex items-center space-x-3 mb-6 bg-white p-4 rounded-xl shadow-sm border border-slate-200">
          <Switch
            checked={showCarbonCredits}
            onCheckedChange={(checked) => {
              setShowCarbonCredits(checked);
              if (!checked && filterType !== '' && filterType !== 'emission') {
                setFilterType(''); // Reset filter if hiding credits
              }
            }}
          />
          <Label className="text-[#003B5C] font-medium cursor-pointer">Include Carbon Credits</Label>
        </div>

        {/* Filters and Actions */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center space-x-2 flex-wrap gap-2">
            <button
              onClick={() => { setFilterType(''); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filterType === '' ? 'bg-[#003B5C] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            >
              All{showCarbonCredits ? '' : ' Emissions'}
            </button>
            {showCarbonCredits && (
              <>
                <button
                  onClick={() => { setFilterType('emission'); setCurrentPage(1); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center ${filterType === 'emission' ? 'bg-red-500 text-white' : 'bg-white border border-slate-200 text-red-600 hover:bg-red-50'}`}
                >
                  <span className="w-2 h-2 bg-red-500 rounded-full mr-2" />
                  Emissions
                </button>
                <button
                  onClick={() => { setFilterType('offset'); setCurrentPage(1); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center ${filterType === 'offset' ? 'bg-emerald-500 text-white' : 'bg-white border border-slate-200 text-emerald-600 hover:bg-emerald-50'}`}
                >
                  <Leaf className="w-3 h-3 mr-2" />
                  All Credits
                </button>
                <button
                  onClick={() => { setFilterType('offset_purchase'); setCurrentPage(1); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center ${filterType === 'offset_purchase' ? 'bg-blue-500 text-white' : 'bg-white border border-slate-200 text-blue-600 hover:bg-blue-50'}`}
                >
                  <Leaf className="w-3 h-3 mr-2" />
                  Purchases
                </button>
                <button
                  onClick={() => { setFilterType('offset_applied'); setCurrentPage(1); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center ${filterType === 'offset_applied' ? 'bg-emerald-600 text-white' : 'bg-white border border-slate-200 text-emerald-700 hover:bg-emerald-50'}`}
                >
                  <Leaf className="w-3 h-3 mr-2" />
                  Applied
                </button>
              </>
            )}
          </div>

          {/* Export Dropdown */}
          <div className="relative">
            <Button
              variant="outline"
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={exportLoading}
            >
              {exportLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Export
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
                <div className="px-3 py-1.5 text-xs font-semibold text-slate-500 uppercase">Current Year (FY{years[0] || new Date().getFullYear()})</div>
                <button
                  onClick={() => {
                    setShowExportMenu(false);
                    exportToCSV(years[0] || new Date().getFullYear());
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-3"
                >
                  <FileText className="w-4 h-4 text-green-600" />
                  <span>Export as CSV</span>
                </button>
                <button
                  onClick={() => {
                    setShowExportMenu(false);
                    exportToXLS(years[0] || new Date().getFullYear());
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-3"
                >
                  <FileText className="w-4 h-4 text-blue-600" />
                  <span>Export as Excel</span>
                </button>

                <div className="border-t border-slate-100 my-1" />
                <div className="px-3 py-1.5 text-xs font-semibold text-slate-500 uppercase">All Years</div>
                <button
                  onClick={() => {
                    setShowExportMenu(false);
                    exportToCSV(null);
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-3"
                >
                  <FileText className="w-4 h-4 text-green-600" />
                  <span>All Years (CSV)</span>
                </button>
                <button
                  onClick={() => {
                    setShowExportMenu(false);
                    exportToXLS(null);
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-3"
                >
                  <FileText className="w-4 h-4 text-blue-600" />
                  <span>All Years (Excel)</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Search & Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search entries..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} className="pl-10" />
            </div>
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className={showFilters ? 'bg-[#003B5C] text-white' : ''}>
              <SlidersHorizontal className="w-4 h-4 mr-2" /> Filters
            </Button>
            <Select value={rowsPerPage.toString()} onValueChange={(v) => { setRowsPerPage(parseInt(v)); setCurrentPage(1); }}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 rows</SelectItem>
                <SelectItem value="25">25 rows</SelectItem>
                <SelectItem value="50">50 rows</SelectItem>
                <SelectItem value="100">100 rows</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {
            showFilters && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="mt-4 pt-4 border-t border-slate-200">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-slate-600 text-sm mb-1 block">Type</Label>
                    <Select value={filterType || 'all'} onValueChange={(v) => { setFilterType(v === 'all' ? '' : v); setCurrentPage(1); }}>
                      <SelectTrigger><SelectValue placeholder="All types" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All types</SelectItem>
                        <SelectItem value="emission">Emission Entries</SelectItem>
                        <SelectItem value="offset">All Carbon Credits</SelectItem>
                        <SelectItem value="offset_purchase">Carbon Credit Purchased</SelectItem>
                        <SelectItem value="offset_applied">Carbon Offset Retired</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-600 text-sm mb-1 block">Category</Label>
                    <Select value={filterCategory || 'all'} onValueChange={(v) => { setFilterCategory(v === 'all' ? '' : v); setCurrentPage(1); }}>
                      <SelectTrigger><SelectValue placeholder="All categories" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All categories</SelectItem>
                        {categories.map(c => <SelectItem key={c} value={c}>{getCategoryInfo(c).label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-600 text-sm mb-1 block">Financial Year</Label>
                    <Select value={filterYear || 'all'} onValueChange={(v) => { setFilterYear(v === 'all' ? '' : v); setCurrentPage(1); }}>
                      <SelectTrigger><SelectValue placeholder="All years" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All years</SelectItem>
                        {years.map(y => <SelectItem key={y} value={y.toString()}>FY {y - 1}/{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button variant="ghost" onClick={() => { setSearchTerm(''); setFilterType(''); setFilterYear(''); setFilterCategory(''); setCurrentPage(1); }} className="text-slate-500">
                      <X className="w-4 h-4 mr-1" /> Clear All
                    </Button>
                  </div>
                </div>
              </motion.div>
            )
          }
        </div >

        {/* Table */}
        < div className="bg-white rounded-xl border border-slate-200 overflow-hidden" >
          {
            paginated.length === 0 ? (
              <div className="text-center py-16">
                <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600">No entries found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto pb-32">
                  {/* pb-32 ensures space for dropdown when few entries */}
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-[#003B5C] cursor-pointer hover:bg-slate-100" onClick={() => handleSort('entry_date')}>
                          Date Added <SortIcon field="entry_date" />
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-[#003B5C] cursor-pointer hover:bg-slate-100" onClick={() => handleSort('date')}>
                          Activity Date <SortIcon field="date" />
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-[#003B5C] cursor-pointer hover:bg-slate-100" onClick={() => handleSort('reporting_period')}>
                          Reporting Period <SortIcon field="reporting_period" />
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-[#003B5C] cursor-pointer hover:bg-slate-100" onClick={() => handleSort('type')}>
                          Type <SortIcon field="type" />
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-[#003B5C] cursor-pointer hover:bg-slate-100" onClick={() => handleSort('name')}>
                          Name <SortIcon field="name" />
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-[#003B5C] cursor-pointer hover:bg-slate-100" onClick={() => handleSort('quantity')}>
                          Quantity <SortIcon field="quantity" />
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-[#003B5C] cursor-pointer hover:bg-slate-100" onClick={() => handleSort('amount')}>
                          Emissions <SortIcon field="amount" />
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-[#003B5C]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {paginated.map((entry) => {
                        const isOffset = entry.type === 'offset_purchase' || entry.type === 'offset_applied';
                        const isPurchase = entry.type === 'offset_purchase';
                        const isApplied = entry.type === 'offset_applied';
                        const catInfo = getCategoryInfo(entry.category);
                        const Icon = catInfo.icon;

                        // Get activity dates for emissions
                        const startDate = entry.raw?.start_date || entry.date;
                        const endDate = entry.raw?.end_date;
                        const entryDateAdded = entry.raw?.entry_date || entry.raw?.created_at;

                        // Calculate reporting period
                        const reportingPeriod = isOffset
                          ? (entry.year ? getFYLabel(entry.year) : '-')
                          : getReportingPeriods(startDate, endDate || startDate);

                        // Determine badge style and label
                        let badgeClass = 'bg-red-100 text-red-700';
                        let badgeLabel = 'Emission Entry Added';
                        let badgeIcon = <Flame className="w-3 h-3 mr-1" />;
                        if (isPurchase) {
                          badgeClass = 'bg-blue-100 text-blue-700';
                          badgeLabel = 'Carbon Credit Purchased';
                          badgeIcon = <Leaf className="w-3 h-3 mr-1" />;
                        } else if (isApplied) {
                          badgeClass = 'bg-emerald-100 text-emerald-700';
                          badgeLabel = 'Carbon Offset Retired';
                          badgeIcon = <Leaf className="w-3 h-3 mr-1" />;
                        }

                        return (
                          <tr key={entry.id} className="hover:bg-slate-50">
                            <td className="px-4 py-3 text-sm text-slate-500 border-l-2 border-transparent group-hover:border-[#003B5C]">
                              <div className="flex flex-col">
                                <span>
                                  {entryDateAdded ? formatEntryDate(entryDateAdded,
                                    currentOrg?.state ? `Australia/${{
                                      NSW: 'Sydney', VIC: 'Melbourne', QLD: 'Brisbane', SA: 'Adelaide',
                                      WA: 'Perth', TAS: 'Hobart', NT: 'Darwin', ACT: 'Sydney'
                                    }[currentOrg.state] || 'Sydney'
                                      }` : 'Australia/Sydney'
                                  ) : '-'}
                                </span>
                                {entryDateAdded && (
                                  <span className="text-xs text-slate-400">
                                    {new Date(entryDateAdded).toLocaleTimeString('en-AU', {
                                      hour: '2-digit', minute: '2-digit', hour12: false, timeZone: currentOrg?.state ? `Australia/${{
                                        NSW: 'Sydney', VIC: 'Melbourne', QLD: 'Brisbane', SA: 'Adelaide',
                                        WA: 'Perth', TAS: 'Hobart', NT: 'Darwin', ACT: 'Sydney'
                                      }[currentOrg.state] || 'Sydney'}` : 'Australia/Sydney'
                                    })}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600">
                              {isOffset ? (
                                new Date(entry.date).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: '2-digit' })
                              ) : (
                                formatActivityDate(startDate, endDate)
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-xs font-medium px-2 py-1 rounded ${reportingPeriod.includes(',')
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-slate-100 text-slate-700'
                                }`}>
                                {reportingPeriod}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center space-x-1.5">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badgeClass}`}>
                                  {badgeIcon}
                                  {badgeLabel}
                                </span>
                                {entry.hasEvidence && (
                                  <span className="text-[#003B5C]" title="Evidence attached">
                                    <Paperclip className="w-3.5 h-3.5" />
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm font-medium text-[#003B5C]">{entry.name}</span>
                            </td>
                            <td className="px-4 py-3 text-right text-sm text-slate-600">
                              {entry.quantity} {entry.unit}
                            </td>
                            <td className={`px-4 py-3 text-right text-sm font-medium ${isApplied || (Math.abs(entry.amount) === 0 && !isPurchase) ? 'text-emerald-600' : isPurchase ? 'text-blue-600' : 'text-red-600'}`}>
                              {isApplied ? '-' : isPurchase ? '' : '+'}
                              {Math.abs(entry.amount) < 0.1 && Math.abs(entry.amount) > 0
                                ? Math.abs(entry.amount).toFixed(4)
                                : Math.abs(entry.amount).toFixed(2)} tCO₂-e
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end relative">
                                {/* Kebab Menu Button */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setOpenActionMenu(openActionMenu === entry.id ? null : entry.id)}
                                  className="text-slate-500 hover:bg-slate-100 h-8 w-8 p-0"
                                  data-testid={`action-menu-${entry.id}`}
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </Button>

                                {/* Dropdown Menu */}
                                {openActionMenu === entry.id && (
                                  <div className="absolute right-0 top-8 z-50 bg-white rounded-lg shadow-lg border border-slate-200 py-1 min-w-[160px]">
                                    {/* View Evidence - for emissions with documents */}
                                    {entry.hasEvidence && !isOffset && (
                                      <button
                                        onClick={async () => {
                                          const storagePath = entry.storagePath || entry.raw?.documents?.[0]?.storage_path;
                                          if (storagePath) {
                                            const { data, error } = await supabase.storage
                                              .from('evidence-vault')
                                              .createSignedUrl(storagePath, 3600);
                                            if (!error && data?.signedUrl) {
                                              window.open(data.signedUrl, '_blank');
                                            } else {
                                              console.error('Signed URL error:', error);
                                              toast.error('Could not access file');
                                            }
                                          } else {
                                            toast.error('Evidence file path not found');
                                          }
                                          setOpenActionMenu(null);
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-2 text-[#003B5C]"
                                      >
                                        <Eye className="w-4 h-4" />
                                        <span>View Evidence</span>
                                      </button>
                                    )}
                                    {/* View Certificate - for offsets with certificate */}
                                    {entry.hasEvidence && isOffset && (
                                      <button
                                        onClick={() => { window.open(entry.evidenceUrl, '_blank'); setOpenActionMenu(null); }}
                                        className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-2 text-[#003B5C]"
                                      >
                                        <Eye className="w-4 h-4" />
                                        <span>View Certificate</span>
                                      </button>
                                    )}

                                    {!isOffset ? (
                                      <>
                                        {!canWithReason(FEATURES.EMISSIONS_EDIT_ENTRY).isHidden && (
                                          <button
                                            onClick={() => handleOpenEdit(entry)}
                                            className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-2 text-blue-600"
                                          >
                                            <Edit className="w-4 h-4" />
                                            <span>Edit</span>
                                          </button>
                                        )}
                                        <button
                                          onClick={() => { fetchEntryHistory(entry.id); setOpenActionMenu(null); }}
                                          className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-2 text-slate-600"
                                        >
                                          <History className="w-4 h-4" />
                                          <span>History</span>
                                        </button>
                                        {/* Show Attach Evidence OR Delete Evidence based on whether evidence exists */}
                                        {!entry.hasEvidence ? (
                                          !canWithReason(FEATURES.EMISSIONS_EVIDENCE_MANAGE).isHidden && (
                                            <button
                                              onClick={() => { setShowEvidenceUpload(entry.id); setOpenActionMenu(null); }}
                                              className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center space-x-2 text-[#74A57F]"
                                            >
                                              <Paperclip className="w-4 h-4" />
                                              <span>Attach Evidence</span>
                                            </button>
                                          )
                                        ) : (
                                          !canWithReason(FEATURES.EMISSIONS_EVIDENCE_MANAGE).isHidden && (
                                            <button
                                              onClick={() => {
                                                setShowEvidenceDeleteConfirm(entry);
                                                setOpenActionMenu(null);
                                              }}
                                              disabled={deletingEvidence}
                                              className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 flex items-center space-x-2 text-red-500"
                                            >
                                              {deletingEvidence ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                              ) : (
                                                <Trash2 className="w-4 h-4" />
                                              )}
                                              <span>Delete Evidence</span>
                                            </button>
                                          )
                                        )}
                                        {!canWithReason(FEATURES.EMISSIONS_DELETE_ENTRY).isHidden && (
                                          <>
                                            <div className="border-t border-slate-100 my-1"></div>
                                            {showDeleteConfirm === entry.id ? (
                                              <div className="px-4 py-2 flex items-center justify-between">
                                                <span className="text-xs text-red-600">Confirm delete?</span>
                                                <div className="flex space-x-1">
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => { handleDelete(entry); setOpenActionMenu(null); }}
                                                    disabled={deletingEntry === entry.id}
                                                    className="text-red-600 hover:bg-red-50 h-6 w-6 p-0"
                                                  >
                                                    {deletingEntry === entry.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                                  </Button>
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setShowDeleteConfirm(null)}
                                                    className="text-slate-500 hover:bg-slate-100 h-6 w-6 p-0"
                                                  >
                                                    <X className="w-3 h-3" />
                                                  </Button>
                                                </div>
                                              </div>
                                            ) : (
                                              <button
                                                onClick={() => setShowDeleteConfirm(entry.id)}
                                                className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 flex items-center space-x-2 text-red-500"
                                              >
                                                <Trash2 className="w-4 h-4" />
                                                <span>Delete</span>
                                              </button>
                                            )}
                                          </>
                                        )}
                                      </>
                                    ) : (
                                      <button
                                        onClick={() => { navigate('/offsets'); setOpenActionMenu(null); }}
                                      >
                                        <Eye className="w-4 h-4" />
                                        <span>Manage Offsets</span>
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
                  <div className="text-sm text-slate-600">
                    Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, sorted.length)} of {sorted.length}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>Previous</Button>
                    <span className="text-sm text-slate-600">Page {currentPage} of {totalPages || 1}</span>
                    <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage >= totalPages}>Next</Button>
                  </div>
                </div>
              </>
            )
          }
        </div >

        {/* Summary */}
        < div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4" >
          {/* Total Data Entries */}
          < div className="bg-gradient-to-br from-slate-500 to-slate-600 rounded-xl p-4 text-white shadow-lg" >
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium opacity-90">Total Data Entries</p>
              <FileText className="w-5 h-5 opacity-80" />
            </div>
            <p className="text-3xl font-bold">{filtered.length}</p>
          </div >

          {/* Total Gross Emissions */}
          < div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white shadow-lg" >
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium opacity-90">Total Gross Emissions</p>
              <Flame className="w-5 h-5 opacity-80" />
            </div>
            <p className="text-3xl font-bold">
              +{filtered.filter(e => e.type === 'emission').reduce((sum, e) => sum + e.amount, 0).toFixed(2)} t
            </p>
          </div >

          {/* Carbon Credits Purchased */}
          < div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-lg" >
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium opacity-90">Credits Purchased</p>
              <ShoppingCart className="w-5 h-5 opacity-80" />
            </div>
            <p className="text-3xl font-bold">
              {filtered.filter(e => e.type === 'offset_purchase').reduce((sum, e) => sum + Math.abs(e.amount), 0).toFixed(2)} t
            </p>
          </div >

          {/* Carbon Offsets Retired */}
          < div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white shadow-lg" >
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium opacity-90">Offsets Retired</p>
              <CheckCircle2 className="w-5 h-5 opacity-80" />
            </div>
            <p className="text-3xl font-bold">
              -{filtered.filter(e => e.type === 'offset_applied').reduce((sum, e) => sum + Math.abs(e.amount), 0).toFixed(2)} t
            </p>
          </div >

          {/* Total Net Emissions */}
          < div className="bg-gradient-to-br from-[#003B5C] to-[#00527a] rounded-xl p-4 text-white shadow-lg" >
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium opacity-90">Total Net Emissions</p>
              <BarChart3 className="w-5 h-5 opacity-80" />
            </div>
            <p className="text-3xl font-bold">
              {(filtered.filter(e => e.type === 'emission').reduce((sum, e) => sum + e.amount, 0) -
                filtered.filter(e => e.type === 'offset_applied').reduce((sum, e) => sum + Math.abs(e.amount), 0)).toFixed(2)} t
            </p>
          </div >
        </div >

        {/* Add Emissions Entry Modal */}
        {
          showAddForm && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                <div className="p-6 border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Add Emissions Entry
                    </h2>
                    <Button variant="ghost" size="sm" onClick={() => setShowAddForm(false)}>
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </div>

                <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                  <div>
                    <Label className="text-slate-700">Category</Label>
                    <Select
                      value={newEntry.category}
                      onValueChange={handleNewEntryCategoryChange}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {categoriesLoading ? (
                          <div className="p-4 text-center text-slate-500 text-sm">Loading categories...</div>
                        ) : (
                          Object.entries(groupedCategories).map(([group, items]) => (
                            <div key={group}>
                              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">{group}</div>
                              {items.map(c => (
                                <SelectItem key={c.value} value={c.value} className="py-2.5 px-3 focus:bg-slate-50">
                                  <div className="flex flex-col w-full text-left">
                                    <div className="flex items-center justify-between w-full">
                                      <div className="flex items-center gap-2">
                                        {c.icon && <c.icon className="w-3.5 h-3.5" style={{ color: c.color }} />}
                                        <span className="font-semibold text-slate-800 text-sm">
                                          {c.label}
                                          <span className="text-slate-400 font-normal ml-1">({c.unit})</span>
                                        </span>
                                      </div>
                                      {c.zeroEmission && (
                                        <span className="text-[9px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold">
                                          Zero
                                        </span>
                                      )}
                                    </div>
                                    {(c.rawRegion || c.rawNote) && (
                                      <div className="text-[10px] text-slate-500 mt-1 ml-5.5 leading-tight">
                                        <span className="font-medium text-slate-400 capitalize">{c.rawRegion}</span>
                                        {c.rawNote && (
                                          <span className="before:content-['•'] before:mx-1.5 before:text-slate-300">
                                            {c.rawNote}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </SelectItem>
                              ))}
                            </div>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-slate-700">
                      Quantity ({getCategoryInfo(newEntry.category)?.unit || ''})
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={newEntry.quantity}
                      onChange={(e) => setNewEntry({ ...newEntry, quantity: e.target.value })}
                      className="mt-1"
                      placeholder="Enter quantity"
                    />
                  </div>

                  {/* Date Type Toggle */}
                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {newEntry.is_date_range ? (
                          <CalendarRange className="w-4 h-4 text-[#003B5C]" />
                        ) : (
                          <Calendar className="w-4 h-4 text-[#003B5C]" />
                        )}
                        <Label className="text-slate-700 font-medium">
                          {newEntry.is_date_range ? 'Date Range' : 'Single Date'}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-slate-500">Point-in-time</span>
                        <Switch
                          checked={newEntry.is_date_range}
                          onCheckedChange={(checked) => setNewEntry({
                            ...newEntry,
                            is_date_range: checked,
                            end_date: checked ? newEntry.end_date || newEntry.start_date : ''
                          })}
                        />
                        <span className="text-xs text-slate-500">Period</span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      {newEntry.is_date_range
                        ? 'Use for invoices/bills covering a period (e.g., monthly electricity)'
                        : 'Use for single-day activities (e.g., fuel purchase, flight)'
                      }
                    </p>
                  </div>

                  {/* Date Fields */}
                  <div className={newEntry.is_date_range ? 'grid grid-cols-2 gap-3' : ''}>
                    <div>
                      <Label className="text-slate-700">
                        {newEntry.is_date_range ? 'Start Date' : 'Activity Date'}
                      </Label>
                      <Input
                        type="date"
                        value={newEntry.start_date}
                        onChange={(e) => setNewEntry({ ...newEntry, start_date: e.target.value })}
                        className="mt-1"
                      />
                    </div>

                    {newEntry.is_date_range && (
                      <div>
                        <Label className="text-slate-700">End Date</Label>
                        <Input
                          type="date"
                          value={newEntry.end_date}
                          min={newEntry.start_date}
                          onChange={(e) => setNewEntry({ ...newEntry, end_date: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                    )}
                  </div>

                  {/* FY Info */}
                  <div className="text-xs text-slate-500 bg-blue-50 p-2 rounded border border-blue-100">
                    {newEntry.is_date_range && newEntry.end_date &&
                      getFinancialYearFromDate(newEntry.start_date) !== getFinancialYearFromDate(newEntry.end_date) ? (
                      <span className="flex items-center">
                        <CalendarRange className="w-3 h-3 mr-1 text-blue-600" />
                        <span className="text-blue-700">
                          This entry spans multiple FYs: <strong>{getReportingPeriods(newEntry.start_date, newEntry.end_date)}</strong>
                          <br />
                          Emissions will be pro-rata allocated to each financial year.
                        </span>
                      </span>
                    ) : (
                      <span>
                        Reporting Period: <strong>{getFYLabel(getFinancialYearFromDate(newEntry.start_date))}</strong>
                      </span>
                    )}
                  </div>

                  <div>
                    <Label className="text-slate-700">Reference Notes (optional)</Label>
                    <Input
                      type="text"
                      value={newEntry.notes}
                      onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })}
                      className="mt-1"
                      placeholder="e.g., Invoice #12345, Bill reference"
                    />
                  </div>

                  <div className="pt-2 border-t border-slate-100">
                    <p className="text-xs text-slate-500 mb-2">
                      Tip: After adding the entry, you can attach a bill or receipt from the entries table.
                    </p>
                  </div>
                </div>

                <div className="p-6 border-t border-slate-100 flex justify-end space-x-3">
                  <Button variant="outline" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddEntry}
                    disabled={addingEntry}
                    className="bg-[#74A57F] hover:bg-[#5F8575] text-white"
                  >
                    {addingEntry ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    Add Entry
                  </Button>
                </div>
              </div>
            </div>
          )
        }

        {/* Evidence Upload Modal with Camera Option */}
        {
          showEvidenceUpload && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Attach Evidence
                  </h2>
                  <Button variant="ghost" size="sm" onClick={closeEvidenceModal}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                <p className="text-slate-600 mb-4">
                  Upload a bill, receipt, or take a photo as evidence for this entry.
                </p>

                {/* Hidden file input */}
                <input
                  type="file"
                  ref={evidenceInputRef}
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setSelectedEvidenceFile(file);
                  }}
                />

                {/* Hidden canvas for photo capture */}
                <canvas ref={canvasRef} className="hidden" />

                {/* Camera Mode */}
                {cameraMode && !capturedImage && (
                  <div className="space-y-4">
                    <div className="relative rounded-lg overflow-hidden bg-black aspect-[4/3]">
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex justify-center space-x-3">
                      <Button
                        onClick={stopCamera}
                        variant="outline"
                        className="border-slate-300"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                      <Button
                        onClick={capturePhoto}
                        className="bg-[#003B5C] hover:bg-[#002A45] text-white"
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        Capture Photo
                      </Button>
                    </div>
                  </div>
                )}

                {/* Captured Image Preview */}
                {capturedImage && (
                  <div className="space-y-4">
                    <div className="relative rounded-lg overflow-hidden bg-slate-100 aspect-[4/3]">
                      <img
                        src={URL.createObjectURL(capturedImage)}
                        alt="Captured"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex justify-center space-x-3">
                      <Button
                        onClick={retakePhoto}
                        variant="outline"
                        className="border-slate-300"
                        disabled={evidenceUploading}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Retake
                      </Button>
                      <Button
                        onClick={() => uploadCapturedImage(showEvidenceUpload)}
                        disabled={evidenceUploading}
                        className="bg-[#74A57F] hover:bg-[#5F8575] text-white"
                      >
                        {evidenceUploading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        Upload Photo
                      </Button>
                    </div>
                  </div>
                )}

                {/* Selected File Preview */}
                {selectedEvidenceFile && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Paperclip className="w-8 h-8 text-blue-500" />
                        <div>
                          <p className="text-sm font-medium text-slate-700 truncate max-w-[200px]">
                            {selectedEvidenceFile.name}
                          </p>
                          <p className="text-xs text-slate-500">
                            {(selectedEvidenceFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedEvidenceFile(null)}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex justify-center space-x-3">
                      <Button
                        onClick={() => setSelectedEvidenceFile(null)}
                        variant="outline"
                        className="border-slate-300 w-1/2"
                        disabled={evidenceUploading}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => handleEvidenceUpload(selectedEvidenceFile, showEvidenceUpload)}
                        disabled={evidenceUploading}
                        className="bg-[#003B5C] hover:bg-[#002A45] text-white w-1/2"
                      >
                        {evidenceUploading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Confirm
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Default View - Select File or Take Photo */}
                {!cameraMode && !capturedImage && !selectedEvidenceFile && (
                  <>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      {/* Select File Option */}
                      <div
                        onClick={() => !evidenceUploading && evidenceInputRef.current?.click()}
                        className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-[#74A57F] hover:bg-[#74A57F]/5 transition-colors"
                      >
                        {evidenceUploading ? (
                          <div className="flex flex-col items-center">
                            <Loader2 className="w-8 h-8 text-[#74A57F] animate-spin mb-2" />
                            <span className="text-slate-600 text-sm">Uploading...</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center">
                            <Upload className="w-8 h-8 text-slate-400 mb-2" />
                            <span className="text-slate-700 font-medium text-sm">Select File</span>
                            <span className="text-xs text-slate-400 mt-1">PDF, JPG, PNG</span>
                          </div>
                        )}
                      </div>

                      {/* Take Photo Option */}
                      <div
                        onClick={() => !evidenceUploading && startCamera()}
                        className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-[#003B5C] hover:bg-[#003B5C]/5 transition-colors"
                      >
                        <div className="flex flex-col items-center">
                          <Camera className="w-8 h-8 text-slate-400 mb-2" />
                          <span className="text-slate-700 font-medium text-sm">Take Photo</span>
                          <span className="text-xs text-slate-400 mt-1">Use camera</span>
                        </div>
                      </div>
                    </div>

                    <p className="text-xs text-slate-500 text-center">
                      Maximum file size: 10MB
                    </p>
                  </>
                )}

                {!cameraMode && !capturedImage && (
                  <div className="mt-4 flex justify-end">
                    <Button variant="outline" onClick={closeEvidenceModal}>
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )
        }

        {/* Data History Modal */}
        {
          showHistoryModal && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col" data-testid="history-modal">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Data History
                    </h2>
                    <p className="text-sm text-slate-500">Audit trail for this entry</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowHistoryModal(null);
                      setEntryHistory([]);
                    }}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                <div className="flex-1 overflow-y-auto p-6">
                  {historyLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-[#003B5C]" />
                      <span className="ml-2 text-slate-600">Loading history...</span>
                    </div>
                  ) : entryHistory.length === 0 ? (
                    <div className="text-center py-8">
                      <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-600">No history records found for this entry.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {entryHistory.map((record, index) => {
                        const isLatest = index === 0;
                        return (
                          <div
                            key={record.id}
                            className={`relative pl-6 pb-4 ${index < entryHistory.length - 1 ? 'border-l-2 border-slate-200' : ''}`}
                          >
                            {/* Timeline dot */}
                            <div className={`absolute left-[-8px] top-0 w-4 h-4 rounded-full border-2 ${record.action === 'created' ? 'bg-green-500 border-green-500' :
                              record.action === 'updated' ? 'bg-blue-500 border-blue-500' :
                                record.action === 'deleted' ? 'bg-red-500 border-red-500' :
                                  record.action === 'evidence_added' ? 'bg-purple-500 border-purple-500' :
                                    record.action === 'evidence_removed' ? 'bg-orange-500 border-orange-500' :
                                      'bg-slate-400 border-slate-400'
                              }`} />

                            <div className="bg-slate-50 rounded-lg p-4">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${record.action === 'created' ? 'bg-green-100 text-green-800' :
                                    record.action === 'updated' ? 'bg-blue-100 text-blue-800' :
                                      record.action === 'deleted' ? 'bg-red-100 text-red-800' :
                                        record.action === 'evidence_added' ? 'bg-purple-100 text-purple-800' :
                                          record.action === 'evidence_removed' ? 'bg-orange-100 text-orange-800' :
                                            'bg-slate-100 text-slate-800'
                                    }`}>
                                    {record.action === 'created' && 'Created'}
                                    {record.action === 'updated' && 'Updated'}
                                    {record.action === 'deleted' && 'Deleted'}
                                    {record.action === 'evidence_added' && 'Evidence Added'}
                                    {record.action === 'evidence_removed' && 'Evidence Removed'}
                                  </span>
                                  {isLatest && (
                                    <span className="ml-2 text-xs text-slate-400">Current</span>
                                  )}
                                </div>
                                <div className="text-xs text-slate-500">
                                  <Clock className="w-3 h-3 inline mr-1" />
                                  {new Date(record.created_at).toLocaleString()}
                                </div>
                              </div>

                              <div className="text-sm space-y-1">
                                <div className="flex items-center space-x-2">
                                  <span className="text-slate-600">Category:</span>
                                  <span className="font-medium text-[#003B5C]">
                                    {getCategoryInfo(record.category).label}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-slate-600">Quantity:</span>
                                  <span className="font-medium text-[#003B5C]">
                                    {record.quantity} {record.unit}
                                  </span>
                                </div>
                                {record.co2_equivalent && (
                                  <div className="flex items-center space-x-2">
                                    <span className="text-slate-600">CO₂-e:</span>
                                    <span className="font-medium text-[#74A57F]">
                                      {record.co2_equivalent.toFixed(4)} tonnes
                                    </span>
                                  </div>
                                )}
                                {record.user_email && (
                                  <div className="flex items-center space-x-2">
                                    <span className="text-slate-600">By:</span>
                                    <span className="font-medium text-slate-700 text-xs">
                                      {record.user_email}
                                    </span>
                                  </div>
                                )}
                                {record.notes && (
                                  <div className="mt-2 text-xs text-slate-500 italic">
                                    {record.notes}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        }

        {/* Edit Entry Modal */}
        {
          showEditModal && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                <div className="p-6 border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Edit Entry
                    </h2>
                    <Button variant="ghost" size="sm" onClick={() => setShowEditModal(null)}>
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </div>

                <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                  <div>
                    <Label className="text-slate-700">Category</Label>
                    <Select
                      value={editFormData.category}
                      onValueChange={handleEditCategoryChange}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {categoriesLoading ? (
                          <div className="p-4 text-center text-slate-500 text-sm">Loading categories...</div>
                        ) : (
                          Object.entries(groupedCategories).map(([group, items]) => (
                            <div key={group}>
                              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">{group}</div>
                              {items.map(c => (
                                <SelectItem key={c.value} value={c.value}>
                                  <span className="flex items-center text-sm">
                                    {c.icon && <c.icon className="w-3.5 h-3.5 mr-2" style={{ color: c.color }} />}
                                    {c.formattedLabel || c.label}
                                  </span>
                                </SelectItem>
                              ))}
                            </div>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-slate-700">
                      Quantity ({getCategoryInfo(editFormData.category)?.unit || ''})
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editFormData.quantity}
                      onChange={(e) => setEditFormData({ ...editFormData, quantity: e.target.value })}
                      className="mt-1"
                      placeholder="Enter quantity"
                    />
                  </div>

                  {/* Date Type Toggle */}
                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {editFormData.is_date_range ? (
                          <CalendarRange className="w-4 h-4 text-[#003B5C]" />
                        ) : (
                          <Calendar className="w-4 h-4 text-[#003B5C]" />
                        )}
                        <Label className="text-slate-700 font-medium">
                          {editFormData.is_date_range ? 'Date Range' : 'Single Date'}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-slate-500">Point-in-time</span>
                        <Switch
                          checked={editFormData.is_date_range}
                          onCheckedChange={(checked) => setEditFormData({
                            ...editFormData,
                            is_date_range: checked,
                            end_date: checked ? editFormData.end_date || editFormData.start_date : ''
                          })}
                        />
                        <span className="text-xs text-slate-500">Period</span>
                      </div>
                    </div>
                  </div>

                  {/* Date Fields */}
                  <div className={editFormData.is_date_range ? 'grid grid-cols-2 gap-3' : ''}>
                    <div>
                      <Label className="text-slate-700">
                        {editFormData.is_date_range ? 'Start Date' : 'Activity Date'}
                      </Label>
                      <Input
                        type="date"
                        value={editFormData.start_date}
                        onChange={(e) => setEditFormData({ ...editFormData, start_date: e.target.value })}
                        className="mt-1"
                      />
                    </div>

                    {editFormData.is_date_range && (
                      <div>
                        <Label className="text-slate-700">End Date</Label>
                        <Input
                          type="date"
                          value={editFormData.end_date}
                          min={editFormData.start_date}
                          onChange={(e) => setEditFormData({ ...editFormData, end_date: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                    )}
                  </div>

                  {/* FY Info */}
                  <div className="text-xs text-slate-500 bg-blue-50 p-2 rounded border border-blue-100">
                    {editFormData.is_date_range && editFormData.end_date &&
                      getFinancialYearFromDate(editFormData.start_date) !== getFinancialYearFromDate(editFormData.end_date) ? (
                      <span className="flex items-center">
                        <CalendarRange className="w-3 h-3 mr-1 text-blue-600" />
                        <span className="text-blue-700">
                          This entry spans multiple FYs: <strong>{getReportingPeriods(editFormData.start_date, editFormData.end_date)}</strong>
                        </span>
                      </span>
                    ) : (
                      <span>
                        Reporting Period: <strong>{getFYLabel(getFinancialYearFromDate(editFormData.start_date))}</strong>
                      </span>
                    )}
                  </div>

                  <div>
                    <Label className="text-slate-700">Reference Notes (optional)</Label>
                    <Input
                      type="text"
                      value={editFormData.notes}
                      onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                      className="mt-1"
                      placeholder="e.g., Invoice #12345, Bill reference"
                    />
                  </div>

                  {/* Evidence Actions */}
                  <div className="pt-4 border-t border-slate-100">
                    <Label className="text-slate-700 mb-2 block">Evidence</Label>
                    {showEditModal.hasEvidence ? (
                      <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center space-x-2">
                          <Paperclip className="w-4 h-4 text-[#74A57F]" />
                          <span className="text-sm text-slate-600">Evidence attached</span>
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              const storagePath = showEditModal.storagePath || showEditModal.raw?.documents?.[0]?.storage_path;
                              if (storagePath) {
                                const { data, error } = await supabase.storage
                                  .from('evidence-vault')
                                  .createSignedUrl(storagePath, 3600);
                                if (!error && data?.signedUrl) {
                                  window.open(data.signedUrl, '_blank');
                                } else {
                                  toast.error('Could not access file');
                                }
                              }
                            }}
                            className="text-[#003B5C]"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setShowEvidenceDeleteConfirm(showEditModal);
                              setShowEditModal(null);
                            }}
                            className="text-red-500 border-red-200 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowEvidenceUpload(showEditModal.id);
                          setShowEditModal(null);
                        }}
                        className="w-full text-[#74A57F] border-[#74A57F] hover:bg-[#74A57F] hover:text-white"
                      >
                        <Paperclip className="w-4 h-4 mr-2" />
                        Attach Evidence
                      </Button>
                    )}
                  </div>
                </div>

                <div className="p-6 border-t border-slate-100 flex justify-between">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowDeleteConfirm(showEditModal.id);
                      setShowEditModal(null);
                    }}
                    className="text-red-500 border-red-200 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Entry
                  </Button>
                  <div className="flex space-x-3">
                    <Button variant="outline" onClick={() => setShowEditModal(null)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleEditSubmit}
                      disabled={editLoading}
                      className="bg-[#74A57F] hover:bg-[#5F8575] text-white"
                    >
                      {editLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                      Save Changes
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )
        }

        {/* Evidence Delete Confirmation Modal */}
        {
          showEvidenceDeleteConfirm && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 animate-in fade-in zoom-in duration-200">
                <div className="flex items-center space-x-3 mb-4 text-red-600">
                  <div className="p-2 bg-red-100 rounded-full">
                    <Trash2 className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Delete Evidence?
                  </h3>
                </div>
                <p className="text-slate-600 mb-6">
                  Are you sure you want to delete this evidence? This action cannot be undone.
                </p>
                <div className="flex space-x-3 w-full">
                  <Button
                    variant="outline"
                    onClick={() => setShowEvidenceDeleteConfirm(null)}
                    className="flex-1"
                    disabled={deletingEvidence}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleDeleteEvidence(showEvidenceDeleteConfirm)}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                    disabled={deletingEvidence}
                  >
                    {deletingEvidence ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    {deletingEvidence ? 'Deleting...' : 'Delete Evidence'}
                  </Button>
                </div>
              </div>
            </div>
          )
        }

        {/* Csv Importer Modal */}
        {showCsvImporter && (
          <CsvImporter
            onClose={() => setShowCsvImporter(false)}
            onSuccess={() => {
              // Reload page or re-fetch data to show new entries
              window.location.reload();
            }}
          />
        )}

        {/* Receipt Scanner Modal */}
        {showReceiptScanner && (
          <ReceiptScanner
            onClose={() => setShowReceiptScanner(false)}
            onDataExtracted={(data) => {
              setShowReceiptScanner(false);
              setNewEntry(prev => ({
                ...prev,
                category: data.category || 'electricity',
                quantity: data.quantity,
                start_date: data.start_date,
                end_date: data.end_date || data.start_date,
                is_date_range: !!data.end_date && data.end_date !== data.start_date,
                unit: data.unit
              }));
              setShowAddForm(true);
            }}
          />
        )}

      </main >
    </div >
  );
};

export default EntriesPage;
